import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const DEBOUNCE_MS = 150;

/**
 * Sticky header search, present on every page — jump straight to a state,
 * county, or ZIP via GET /api/search (server/src/geo/index.js's search()),
 * rather than the per-page .search inputs on StateList/CountyList/ZipTable,
 * which only filter whatever list is already loaded on that page.
 */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const boxRef = useRef(null);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    const id = setTimeout(() => {
      fetch(`/api/search?q=${encodeURIComponent(q)}`)
        .then((res) => res.json())
        .then((data) => {
          setResults(data);
          setOpen(true);
          setActiveIndex(-1);
        })
        .catch(() => setResults([]));
    }, DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const go = (result) => {
    if (!result) return;
    // On the map, fly there instead of leaving for the list pages; the map
    // reads ?go=<list path> (e.g. /TX/travis?zip=78701) and zooms to it.
    if (location.pathname === '/') navigate(`/?go=${encodeURIComponent(result.path)}`);
    else navigate(result.path);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[activeIndex] ?? results[0]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="global-search" ref={boxRef}>
      <input
        className="global-search-input"
        type="text"
        placeholder="Search any state, county, or ZIP code…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && results.length > 0 && (
        <div className="global-search-results">
          {results.map((r, i) => (
            <div
              key={r.path}
              className={`global-search-result${i === activeIndex ? ' active' : ''}`}
              onMouseDown={() => go(r)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className={`global-search-type t-${r.type}`}>{r.type}</span>
              {r.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
